import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { VehicleCard } from '../../components/Cards';
import {
  Button, Empty, Loading, Row, SectionHeader,
} from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import Gallery from '../../components/Gallery';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';
import { currency, timeAgo, titleCase } from '../../utils/format';
import { fileUrl } from '../../api/client';

const VehicleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enquiring, setEnquiring] = useState(false);
  const [enquired, setEnquired] = useState(false);

  useEffect(() => {
    setLoading(true);
    setEnquired(false);
    api.vehicles.detail(id)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loading text="Loading vehicle…" />;
  if (!data) {
    return <Empty emoji="🚗" title="Vehicle not found" actionLabel="Go back" onAction={() => navigate(-1)} />;
  }

  const { vehicle, similar } = data;
  const images = vehicle.images || [];

  const enquire = async () => {
    setEnquiring(true);
    try {
      const res = await api.vehicles.enquire(id, { message: 'I am interested in this vehicle.' });
      setEnquired(true);
      const phone = res.data && res.data.sellerPhone;
      toast.success(
        phone ? `The seller has been notified. Contact: ${phone}` : 'The seller has been notified.',
        'Enquiry sent',
      );
    } catch (err) {
      toast.error(err.message, 'Could not send enquiry');
    } finally {
      setEnquiring(false);
    }
  };

  return (
    <div className="has-action-bar">
      <PageHeader title={vehicle.title} subtitle={`Listed ${timeAgo(vehicle.createdAt)}`} />

      <div className="split">
        <div>
          <Gallery
            images={images}
            fallback="🚗"
            emptyLabel="No photo for this listing yet"
            badge={vehicle.isSold ? (
              <span
                style={{
                  position: 'absolute', top: 12, left: 12, background: colors.danger,
                  color: '#fff', padding: '4px 10px', borderRadius: 8, fontWeight: 800, fontSize: 11.5,
                }}
              >
                SOLD
              </span>
            ) : null}
          />
          {vehicle.video ? (
            <div style={{ marginTop: 14 }}>
              <div className="t-small" style={{ fontWeight: 700, marginBottom: 6 }}>🎬 Walkround video</div>
              <video src={fileUrl(vehicle.video)} controls style={{ width: '100%', maxHeight: 420, borderRadius: 14, background: '#000' }} />
            </div>
          ) : null}

          <Reveal style={{ marginTop: 16 }}>
            <div className="t-small">
              {[vehicle.brand, vehicle.model, vehicle.year].filter(Boolean).join(' · ')}
            </div>

            <div
              className="grid"
              style={{
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(104px, 45%), 1fr))',
                gap: 1, background: colors.line, borderRadius: 14,
                overflow: 'hidden', marginTop: 16,
              }}
            >
              {[
                ['Year', vehicle.year],
                ['Driven', vehicle.kmDriven ? `${vehicle.kmDriven.toLocaleString('en-IN')} km` : '—'],
                ['Fuel', titleCase(vehicle.fuelType)],
                ['Transmission', titleCase(vehicle.transmission)],
                ['Owners', vehicle.owners],
                ['Condition', titleCase(vehicle.condition)],
              ].map(([label, value]) => (
                <div key={label} style={{ background: '#fff', padding: 14 }}>
                  <div className="t-tiny">{label.toUpperCase()}</div>
                  <div className="t-body" style={{ fontWeight: 700, marginTop: 2 }}>{String(value || '—')}</div>
                </div>
              ))}
            </div>

            {vehicle.description ? (
              <>
                <h3 className="h3" style={{ marginTop: 24 }}>Description</h3>
                <p className="t-body" style={{ marginTop: 8, lineHeight: 1.7 }}>{vehicle.description}</p>
              </>
            ) : null}
          </Reveal>
        </div>

        <div className="split-side">
          <div className="action-bar">
            <div className="action-main">
              <div style={{ fontSize: 22, fontWeight: 800 }}>{currency(vehicle.price)}</div>
              {vehicle.negotiable ? <div className="t-tiny">Negotiable</div> : null}
            </div>

            <div className="action-cta">
              <Button
                title={enquired ? 'Enquiry sent ✓' : 'I am interested'}
                icon="💬"
                full
                loading={enquiring}
                disabled={enquired || vehicle.isSold}
                onClick={enquire}
              />
            </div>

            {vehicle.contactPhone ? (
              <div className="action-extra" style={{ marginTop: 10 }}>
                <Button
                  title="Call seller"
                  icon="📞"
                  variant="success"
                  full
                  onClick={() => { window.location.href = `tel:${vehicle.contactPhone}`; }}
                />
              </div>
            ) : null}
          </div>

          <div className="card sh-sm action-extra" style={{ marginTop: 16 }}>
            <h3 className="t-title" style={{ marginBottom: 6 }}>Seller</h3>
            <Row label="Name" value={vehicle.contactName || (vehicle.seller && vehicle.seller.name)} />
            {vehicle.partner && vehicle.partner.businessName ? (
              <Row label="Dealer" value={vehicle.partner.businessName} />
            ) : null}
            <Row label="Location" value={vehicle.city || '—'} />
            <Row label="Enquiries" value={String(vehicle.enquiryCount || 0)} />
          </div>
        </div>
      </div>

      {similar && similar.length ? (
        <>
          <SectionHeader title="Similar vehicles" />
          <div className="rail">
            {similar.map((v) => (
              <VehicleCard
                key={v._id}
                vehicle={v}
                width={240}
                onClick={() => navigate(`/vehicles/${v._id}`)}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default VehicleDetailPage;
